import type { RemontaOrdemServico } from "@shared/schema";
import type { ServiceType } from "@shared/pre-analise/types";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { SERVICE_LABELS } from "./dossie-form";
import { Link } from "wouter";
import { ArrowRight } from "lucide-react";

export const ORDER_STATUS_LABELS: Record<string, string> = {
  rascunho: "Rascunho", solicitado: "Solicitado", aguardando_documentos: "Aguardando documentos",
  em_pre_analise: "Em conferência", pendencia: "Pendência", apto_para_execucao: "Apto para execução",
  em_execucao: "Em execução", em_revisao: "Em revisão", concluido: "Concluído", cancelado: "Cancelado",
};
const STATUS_CLASSES: Record<string, string> = {
  concluido: "bg-emerald-100 text-emerald-900", cancelado: "border border-slate-300 bg-white text-slate-700",
  pendencia: "bg-amber-100 text-amber-900", aguardando_documentos: "bg-amber-100 text-amber-900",
};
const RESULT_LABELS: Record<string, string> = {
  apto_para_execucao: "Apto para execução", pendencia_documental: "Pendência documental",
  inviavel_tecnicamente: "Inviável tecnicamente", fora_do_escopo_contratado: "Fora do escopo contratado",
};

const date = (value: string | Date | null | undefined) =>
  value ? new Date(typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value).toLocaleDateString("pt-BR") : null;

/** Ordens do Dossiê em sequência: a ativa em destaque, concluídas e canceladas como histórico. */
export function OrdensServicoTimeline({ orders }: { orders: RemontaOrdemServico[] }) {
  if (!orders.length) {
    return <p className="text-sm text-muted-foreground">Nenhum serviço solicitado neste Dossiê.</p>;
  }
  const sorted = [...orders].sort((a, b) => new Date(a.criadoEm).getTime() - new Date(b.criadoEm).getTime());
  return (
    <ol className="relative space-y-3 border-l border-slate-200 pl-5" aria-label="Serviços do Dossiê">
      {sorted.map((order) => {
        const active = !["concluido", "cancelado"].includes(order.status);
        const details = [
          ["Ordem de serviço do cliente", order.numeroOrdemExterna],
          ["Regulador/perito do cliente", order.reguladorExterno],
          ["Vistoria prevista", date(order.vistoriaPrevistaEm)],
          ["Prazo do laudo/relatório", date(order.prazoEntregaEm)],
          ["Solicitado em", date(order.solicitadoEm)],
          ["Concluído em", date(order.concluidoEm)],
          ["Resultado", order.resultado ? RESULT_LABELS[order.resultado] ?? order.resultado : null],
          ["Motivo do cancelamento", order.motivoCancelamento],
        ].filter(([, value]) => value) as [string, string][];
        return (
          <li key={order.id} className="relative">
            <span aria-hidden="true" className={cn(
              "absolute -left-[1.6rem] top-4 h-3 w-3 rounded-full border-2 border-white",
              active ? "bg-primary ring-2 ring-primary/30" : order.status === "concluido" ? "bg-emerald-600" : "bg-slate-400",
            )} />
            <article className={cn("rounded-md border bg-white p-4", active && "border-primary/40 shadow-sm")}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-slate-900">{SERVICE_LABELS[order.tipo as ServiceType] ?? order.tipo}</h3>
                <Badge className={STATUS_CLASSES[order.status] ?? "bg-blue-100 text-blue-900"}>
                  {active && <span className="sr-only">Em andamento: </span>}{ORDER_STATUS_LABELS[order.status] ?? order.status}
                </Badge>
              </div>
              {details.length > 0 && (
                <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
                  {details.map(([label, value]) => (
                    <div key={label} className="min-w-0">
                      <dt className="text-xs text-muted-foreground">{label}</dt>
                      <dd className="[overflow-wrap:anywhere] text-slate-900">{value}</dd>
                    </div>
                  ))}
                </dl>
              )}
              <Link href={`/ordens-servico/${order.id}`} className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline">Abrir Ordem<ArrowRight aria-hidden="true" className="h-4 w-4" /></Link>
            </article>
          </li>
        );
      })}
    </ol>
  );
}
